import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/lib/api-client'
import { errorMessage } from '@/lib/errors'
import type { CartItem, CheckoutResult } from '@/types/api'

export function useCart(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['cart'],
    queryFn: () => api.get<CartItem[]>('/cart'),
    enabled: options?.enabled ?? true,
  })
}

export function useAddToCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (courseId: string) =>
      api.post<CartItem>('/cart/items', { courseId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      toast.success('Added to cart')
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not add this to your cart.')),
  })
}

export function useRemoveFromCart() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (courseId: string) => api.delete(`/cart/items/${courseId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      toast.success('Removed from cart')
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not remove this from your cart.')),
  })
}

export function useCheckout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<CheckoutResult>('/cart/checkout'),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      void queryClient.invalidateQueries({ queryKey: ['enrollments'] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast.success(
        data.order
          ? 'Order placed — pending payment confirmation'
          : "You're enrolled! Start learning now.",
      )
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Checkout failed. Please try again.')),
  })
}
